import { engagementTabPath, RETIRED_SURFACE_TABS } from '@/features/engagement/engagement-tabs';
import { limitsTabPath, RETIRED_LIMIT_SURFACE_TABS } from '@/features/limits/limits-tabs';

import type { PageOrRedirectRouteEntry } from './route-table.types';

/**
 * Every in-shell path that only forwards somewhere else.
 *
 * Split out of `route-table.in-shell.ts` for the ~300-line ceiling, along a real
 * seam rather than an arbitrary one: these entries render nothing, take no
 * component, and — unlike the page entries — their relative ORDER is immaterial,
 * because every one matches an exact path with no `:param` that could shadow a
 * sibling. That is what makes moving them safe when moving pages would not be.
 *
 * They are spread AFTER the page entries in the table, preserving the original
 * relative order of pages to redirects. No redirect here shares a path with a
 * page, so the placement is behaviour-neutral — `route-table-coverage.test.tsx`
 * and `routes.test.tsx` both still exercise the composed table.
 *
 * Each keeps the comment explaining what replaced it. That narrative is the
 * reason these are worth reading at all: a bare list of forwards tells you
 * nothing about why the old URL existed.
 */
export const inShellRedirectEntries: readonly PageOrRedirectRouteEntry[] = [
  // The audit trail and the usage/cost log were two pages answering questions
  // the same person asks in the same sitting — "who changed this setting?" and
  // "why did their bill spike?" — so choosing between them required already
  // knowing which of the two had recorded the thing you were looking for. They
  // are now the two tabs of `/system-logs`.
  //
  // All three preserve the search: an audit bookmark pins filters, and a usage
  // one pins filters and a page index. The usage redirect names `?tab=usage`
  // itself, since that reading is not the default tab, and
  // `RedirectPreservingSearch` MERGES the two queries — the destination's own
  // `tab` wins, everything the bookmark carried rides along. This sentence was
  // false for a while: the redirect concatenated, so naming a tab and keeping
  // the search were mutually exclusive and the usage one silently dropped it.
  {
    kind: 'redirect',
    placement: 'in-shell',
    path: '/audit',
    to: '/system-logs',
    preserveSearch: true,
  },
  // AL-5's event permalink. `RedirectPreservingSearch` interpolates `:eventId`
  // via `generatePath`, so a link already sent or filed in a ticket keeps
  // opening the same event rather than one literally named ":eventId".
  {
    kind: 'redirect',
    placement: 'in-shell',
    path: '/audit/:eventId',
    to: '/system-logs/event/:eventId',
    preserveSearch: true,
  },
  {
    kind: 'redirect',
    placement: 'in-shell',
    path: '/usage-logs',
    to: '/system-logs?tab=usage',
    preserveSearch: true,
  },
  // App-menu restructure — the standalone Catalog browse is the Tools Catalog
  // hub's "Catalog" tab; the old path redirects. The deep-dive keeps its route.
  { kind: 'redirect', placement: 'in-shell', path: '/catalog', to: '/tools?tab=catalog' },
  // Documentation-hub consolidation — `/documentation` → the auto-generated API
  // reference. It briefly lived at `/docs?tab=reference`; DE-301 § 7.2 moved it
  // beside Tools, where its audience already is, so this forwards one hop
  // further rather than through the retired tab. A chain of two redirects would
  // work and would also mean the next reader has to follow it to find out where
  // the page went.
  { kind: 'redirect', placement: 'in-shell', path: '/documentation', to: '/tools?tab=reference' },
  // DE-301 § 7.2 — `/site-manual` retires into the documentation hub's Articles
  // view, where the review queue already describes the same pages of the same
  // product. Not merely an adjacent forward: the surface moved (the manual is a
  // panel of that view now), the sidebar row is gone and the Labs entry that
  // gated it was renamed off the dead route's name.
  { kind: 'redirect', placement: 'in-shell', path: '/site-manual', to: '/docs/articles' },
  // Agent IA consolidation (Phase 6) — these four surfaces folded into the Tools
  // hub; the standalone pages are retired and their old paths redirect here.
  { kind: 'redirect', placement: 'in-shell', path: '/tool-availability', to: '/tools' },
  { kind: 'redirect', placement: 'in-shell', path: '/abilities', to: '/tools' },
  { kind: 'redirect', placement: 'in-shell', path: '/governance', to: '/tools' },
  { kind: 'redirect', placement: 'in-shell', path: '/custom-endpoints', to: '/tools' },
  // App-menu restructure — external MCP (BYOMCP) server management moved into the
  // Tools Catalog hub's "External MCP servers" tab; the old path redirects.
  { kind: 'redirect', placement: 'in-shell', path: '/byomcp', to: '/tools?tab=servers' },
  // `/components` → `/widget`, RESTORED (2026-08-28, operator decision) after
  // Adaptive Compositions D1 had briefly reclaimed the path for the block
  // showroom. The showroom is retired: its catalogue was reference material, its
  // preview console moved to `/experience`, and its one real control — the
  // registered-component promote/enable switches — moved into the Design Studio.
  //
  // So the Studio is where somebody who saved this path was going: it is the only
  // surface that shows a registered component at all, and now the only one that
  // governs it. The preview console is the one thing this destination does NOT
  // carry, and `/experience` owns it beside the settings that shape it.
  { kind: 'redirect', placement: 'in-shell', path: '/components', to: '/widget' },
  // `/impact` is deliberately ABSENT from this table. The Analytics hub absorbed
  // every panel that page carried (Business Success Visibility § 2.4), and the
  // operator's call (D-2) was NO redirect: the page was operator-only behind a
  // default-OFF flag, so no customer ever held a bookmark to redirect. A
  // redirect was added here during the 2026-08-28 main merge and removed again
  // — `routes.test.tsx` asserts the 404 for exactly that reason, since every
  // component the page rendered still exists and re-adding the route compiles.
  // SYN-133 / ADR-0154 — the standalone Cost page folded into Settings → Billing.
  { kind: 'redirect', placement: 'in-shell', path: '/cost', to: '/settings/billing' },
  // Limits & alerts consolidation — spend caps, usage alarms and rate limits
  // answered one question between them ("what stops this workspace costing or
  // doing too much") across three pages, so an operator had to know which of
  // three held the control they wanted. `/limits` is that one page; all three
  // old paths forward to it, because each is a URL somebody has bookmarked and
  // every control they held still exists there.
  //
  // Each names the TAB that absorbed it, not the page root. All three used to
  // forward bare, and `/limits` read no `?tab=` at all, so every one of them
  // landed on Budget: somebody who had saved the alarms page arrived at
  // spending ceilings and had to work out that alarms were now a tab — which
  // is the cost the consolidation was supposed to remove, re-introduced by the
  // redirect. The destinations come from `RETIRED_LIMIT_SURFACE_TABS`, which is
  // also what the page's tab vocabulary and its tests read, so the two cannot
  // disagree about where a bookmark goes.
  //
  // `preserveSearch` is ON for the same reason it is on the Engagement three:
  // the fold MERGES destination-wins, so the tab named here always survives
  // while anything else the bookmark carried rides along.
  {
    kind: 'redirect',
    placement: 'in-shell',
    path: '/spend-caps',
    to: limitsTabPath(RETIRED_LIMIT_SURFACE_TABS['/spend-caps'] ?? 'budget'),
    preserveSearch: true,
  },
  {
    kind: 'redirect',
    placement: 'in-shell',
    path: '/usage-alarms',
    to: limitsTabPath(RETIRED_LIMIT_SURFACE_TABS['/usage-alarms'] ?? 'alerts'),
    preserveSearch: true,
  },
  {
    kind: 'redirect',
    placement: 'in-shell',
    path: '/rate-limits',
    to: limitsTabPath(RETIRED_LIMIT_SURFACE_TABS['/rate-limits'] ?? 'traffic'),
    preserveSearch: true,
  },
  // Connections consolidation — the per-tenant MCP server surface lives only under
  // the Connections hub's "MCP server" tab now; the old path redirects.
  { kind: 'redirect', placement: 'in-shell', path: '/mcp', to: '/connections?tab=mcp' },
  { kind: 'redirect', placement: 'in-shell', path: '/settings/widget', to: '/widget' },
  // Connections consolidation — the widget embedding-origin allowlist lives only
  // under the Connections hub's "Widget origins" tab now; the old path redirects.
  {
    kind: 'redirect',
    placement: 'in-shell',
    path: '/settings/widget-origins',
    to: '/connections?tab=origins',
  },
  // Q-D2 — white-label custom-domain config (AF-208); lives under the Connections
  // hub's "Custom domain" tab now; the old path redirects.
  {
    kind: 'redirect',
    placement: 'in-shell',
    path: '/settings/custom-domain',
    to: '/connections?tab=domain',
  },
  // Agent IA consolidation — the old A17 /settings/custom-endpoints bookmark now
  // lands on the Tools hub (custom-endpoint CRUD lives there).
  { kind: 'redirect', placement: 'in-shell', path: '/settings/custom-endpoints', to: '/tools' },
  // Connections consolidation — BYOK + identity federation live only under the
  // Connections hub now; the old paths redirect.
  { kind: 'redirect', placement: 'in-shell', path: '/settings/byok', to: '/connections?tab=byok' },
  {
    kind: 'redirect',
    placement: 'in-shell',
    path: '/settings/federation',
    to: '/connections?tab=federation',
  },
  // Renamed from /user-learning (ADR-0228). The old path redirects, preserving any
  // `?tab=` so bookmarks keep working.
  {
    kind: 'redirect',
    placement: 'in-shell',
    path: '/user-learning',
    to: '/users',
    preserveSearch: true,
  },
  // Insights folded into Conversation Logs; the old path redirects.
  { kind: 'redirect', placement: 'in-shell', path: '/insights', to: '/conversation-logs' },
  // The Engagement merge — three surfaces describing one visitor experience
  // became one page with three tabs, so each old path lands on the tab that
  // absorbed it. The destinations come from `RETIRED_SURFACE_TABS`, which is
  // also what the tab vocabulary and its tests read, so the two cannot disagree
  // about where a bookmark goes.
  //
  // `preserveSearch` is ON for all three, which it could not be until the
  // redirect learned to MERGE. `/proactive?nudge=<id>` is a real deep link the
  // drawer hands people, and dropping the query would land them on the tab with
  // the drawer closed — the surface but not the thing they saved. The old
  // concatenating redirect could not carry it, because these destinations name a
  // tab of their own: `/engagement?tab=nudges` + `?nudge=x` came out as
  // `?tab=nudges?nudge=x`, one param whose value is nonsense.
  //
  // `/page-rules` is the case that decides the merge DIRECTION. It carried its
  // own `?tab=all|specific`, and both halves live on the placement tab now (the
  // defaults as the state card, the rules below it). Destination-wins is what
  // makes that safe: `tab=placement` replaces the stale `tab=all` instead of
  // fighting it, while anything else on the URL still travels.
  {
    kind: 'redirect',
    placement: 'in-shell',
    path: '/starters',
    to: engagementTabPath(RETIRED_SURFACE_TABS['/starters'] ?? 'suggestions'),
    preserveSearch: true,
  },
  {
    kind: 'redirect',
    placement: 'in-shell',
    path: '/proactive',
    to: engagementTabPath(RETIRED_SURFACE_TABS['/proactive'] ?? 'nudges'),
    preserveSearch: true,
  },
  {
    kind: 'redirect',
    placement: 'in-shell',
    path: '/page-rules',
    to: engagementTabPath(RETIRED_SURFACE_TABS['/page-rules'] ?? 'placement'),
    preserveSearch: true,
  },
];
